import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InventoryMetrics } from '../display';
import { ProductStatisticsSection } from '../display';
import { LoadingState } from '../feedback';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { InventoryProduct } from '../../models/InventoryApiResponse';
import { InventoryFilters } from '../../models/InventoryFilters';
import { ColumnDef } from '@tanstack/react-table';
import { useInventoryData } from '../../hooks/useInventoryData';
import { useQueryParams } from '@shared/hooks/useQueryParams';

/**
 * Hook para debounce personalizado
 * Custom debounce hook
 */
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Componente contenedor principal del módulo Inventory
 * Maneja la lógica de estado y datos del módulo
 * Main container component for Inventory module - Handles state and data logic
 */
export const Main = () => {
  const { t } = useTranslation('inventoryModule');

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  // State to control that initial loading spinner is shown only once
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Query params para sincronizar búsqueda y filtro de ventas
  // Query params to sync search and sales filter
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    salesFilter: { type: 'string', default: 'all' },
  });

  // Estados de búsqueda y filtro de ventas sincronizados con la URL
  // Search and sales filter states synced with the URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [inputSearchTerm, setInputSearchTerm] = useState(queryParams.search ?? '');
  const [salesFilter, setSalesFilter] = useState<'all' | 'sold_last_30_days' | 'not_sold_last_30_days'>(
    (queryParams.salesFilter as 'all' | 'sold_last_30_days' | 'not_sold_last_30_days') || 'all',
  );

  // 🎯 Debounce del término de búsqueda para consultas automáticas a la API
  // Debounce search term for automatic API queries
  const debouncedSearchTerm = useDebounce(inputSearchTerm, 500);

  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
    setInputSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    const filter = (queryParams.salesFilter as 'all' | 'sold_last_30_days' | 'not_sold_last_30_days') || 'all';
    setSalesFilter(filter);
  }, [queryParams.salesFilter]);

  // 🎯 Efecto para ejecutar búsqueda cuando cambia el término con debounce
  // Effect to execute search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setSearchTerm(debouncedSearchTerm);
      if (queryParams.search !== debouncedSearchTerm) {
        updateQueryParams({ search: debouncedSearchTerm || undefined });
      }
      console.log('🔍 Main - Debounced search executed:', debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, searchTerm, queryParams.search, updateQueryParams]);

  // Convertir búsqueda y filtro de ventas a InventoryFilters
  // Convert search and sales filter to InventoryFilters
  const inventoryFilters: InventoryFilters = useMemo(() => {
    const filters: InventoryFilters = {};

    // Agregar búsqueda si existe
    // Add search if exists
    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }

    // Agregar filtro de ventas si no es 'all'
    // Add sales filter if not 'all'
    if (salesFilter && salesFilter !== 'all') {
      filters.sales_filter = salesFilter;
    }

    return filters;
  }, [searchTerm, salesFilter]);

  const { products, summary, isLoading, isFetching } = useInventoryData({ filters: inventoryFilters });

  // 🎯 Marcar que la carga inicial terminó cuando los datos se cargan por primera vez
  // Mark that initial load finished when data loads for the first time
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Datos mostrados en la tabla (persisten mientras se hace fetching)
  // Data shown in the table (persists while fetching)
  const [displayProductData, setDisplayProductData] = useState<InventoryProduct[]>([]);

  useEffect(() => {
    // Solo actualizar los datos mostrados cuando termina el fetching
    // Only update displayed data when fetching has finished
    if (!isFetching) {
      setDisplayProductData(products);
    }
  }, [products, isFetching]);

  // Handler para búsqueda - Actualiza el estado del input (se ejecutará automáticamente con debounce)
  // Search handler - Updates input state (will execute automatically with debounce)
  const handleSearch = useCallback((value: string) => {
    setInputSearchTerm(value);
    console.log('🔍 Main - Input search changed:', value);
  }, []);

  // Handler para cambio del filtro de ventas
  // Handler for sales filter change
  const handleSalesFilterChange = useCallback(
    (value: 'all' | 'sold_last_30_days' | 'not_sold_last_30_days') => {
      setSalesFilter(value);
      if (queryParams.salesFilter !== value) {
        updateQueryParams({ salesFilter: value === 'all' ? undefined : value });
      }
      console.log('🔍 Main - Sales filter changed:', value);
    },
    [queryParams.salesFilter, updateQueryParams],
  );

  // Columnas para ColumnSelector
  // Columns for ColumnSelector
  const tableColumns: ColumnDef<InventoryProduct>[] = useMemo(
    () => [
      { id: 'name', header: t('table.product') },
      { id: 'stock_available', header: t('table.stock_available') },
      { id: 'inventory_value', header: t('table.inventory_value') },
      { id: 'avg_daily_sales', header: t('table.avg_daily_sales') },
      { id: 'days_of_stock_remaining', header: t('table.days_of_stock') },
    ],
    [t],
  );

  const [selectedColumns, setSelectedColumns] = useState<string[]>(tableColumns.map((col) => col.id as string));

  const handleColumnsChange = useCallback((newSelected: string[]) => {
    setSelectedColumns(newSelected);
  }, []);

  // Detectar si hay filtros activos (búsqueda o filtro de ventas)
  // Detect if there are active filters (search or sales filter)
  const hasActiveFilters = useMemo(() => {
    return (searchTerm && searchTerm.trim().length > 0) || (salesFilter && salesFilter !== 'all');
  }, [searchTerm, salesFilter]);

  // Detectar si todos los datos están en 0 (solo si NO hay filtros activos)
  // Detect if all data is zero (only if there are NO active filters)
  const isAllDataZero = useMemo(() => {
    // Si hay filtros activos, no mostrar el EmptyView global
    // If there are active filters, don't show global EmptyView
    if (hasActiveFilters) return false;

    // Verificar summary
    if (summary) {
      const hasData =
        summary.total_products > 0 ||
        summary.total_inventory_value > 0 ||
        summary.total_sales_last_30_days > 0 ||
        summary.average_daily_sales > 0;
      if (hasData) return false;
    }

    // Verificar products
    if (products && products.length > 0) {
      const hasProductData = products.some(
        (product: InventoryProduct) =>
          product.stock_available > 0 || product.total_sales > 0 || product.inventory_value > 0,
      );
      if (hasProductData) return false;
    }

    // Si llegamos aquí, todos los datos están en 0 o no hay datos
    return true;
  }, [summary, products, hasActiveFilters]);

  // Mostrar spinner solo en la carga inicial
  // Show spinner only on initial load
  if (isLoading && isInitialLoad) {
    return <LoadingState />;
  }

  // Mostrar EmptyView si todos los datos están en 0
  // Show EmptyView if all data is zero
  if (isAllDataZero) {
    return (
      <div className="px-6 py-4">
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.PRODUCTS}
          title={t('empty.no_data.title', 'No hay datos disponibles')}
          description={t('empty.no_data.description', 'No se encontraron datos de inventario.')}
        />
      </div>
    );
  }

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Métricas de inventario - Inventory metrics */}
      <InventoryMetrics summary={summary} products={products} isLoading={isLoading} />

      {/* Sección de estadísticas por producto - Product statistics section */}
      <ProductStatisticsSection
        data={displayProductData}
        total={displayProductData.length}
        handleSearch={handleSearch}
        onSalesFilterChange={handleSalesFilterChange}
        columns={tableColumns}
        selectedColumns={selectedColumns}
        onColumnsChange={handleColumnsChange}
        searchTerm={inputSearchTerm}
        salesFilter={salesFilter}
        isFetching={isFetching}
      />
    </div>
  );
};
